/**
 * Specialty layer — maps a DICOM Modality (0008,0060) to one of
 * the four clinical specialties VaultPod serves, and returns the UI
 * config the viewer should adopt for that specialty.
 *
 * The viewer chrome, tool set, report template and default render
 * preset all key off the specialty. Everything else (DICOMweb
 * backend, auth, storage) stays identical — one engine, four
 * skins.
 *
 * Detection is single-source: the active series' modality code.
 * Multi-specialty studies (e.g. a RT-plan with a linked CT) pick
 * the MOST specialised specialty (RT > pathology > endoscopy >
 * radiology).
 */

export type Specialty =
  | "radiology"
  | "pathology"
  | "endoscopy"
  | "radiotherapy";

export interface SpecialtyConfig {
  id:        Specialty;
  label:     string;          // human-facing name ("Radiology")
  accent:    string;          // tailwind class suffix for badge tinting
  /** Subset of `ToolId` that makes sense in this specialty — the
   *  ToolPalette uses this to hide irrelevant groups (e.g. ellipse HU
   *  on an endoscopy video is nonsense). */
  toolgroups: Array<"view" | "measure" | "label" | "ml" | "video" | "wsi" | "rt">;
  /** Default hanging-protocol preset hint; the 2-D preset bank picks
   *  the best match for this tag. */
  hangingHint: string;
  /** Report template name — used to seed the AI report draft panel
   *  with the right headings (Radiology Impression vs Pathology
   *  Microscopic vs Endoscopy Findings vs RT Plan Review). */
  reportTemplate:
    | "radiology.impression"
    | "pathology.microscopic"
    | "endoscopy.findings"
    | "radiotherapy.plan_review";
  /** Short description used in the specialty picker. */
  description: string;
}

/** Modality → specialty. Unknown codes fall back to radiology — the
 *  safest default since that's where the bulk of DICOM objects live. */
const MOD_TO_SPECIALTY: Record<string, Specialty> = {
  // Radiology — every "pixel image from a machine" acquisition.
  CT: "radiology",  MR: "radiology",  CR: "radiology",  DX: "radiology",
  MG: "radiology",  US: "radiology",  NM: "radiology",  PT: "radiology",
  XA: "radiology",  RF: "radiology",  IO: "radiology",  PX: "radiology",
  BMD: "radiology", BDUS: "radiology", TG: "radiology",
  IVUS: "radiology", IVOCT: "radiology", OCT: "radiology", OP: "radiology",
  OPT: "radiology", OPV: "radiology", OPM: "radiology", SM: "pathology",

  // Pathology digital imaging.
  GM: "pathology",   // General Microscopy

  // Endoscopy — video/photo from a scope.
  ES: "endoscopy",

  // Radiotherapy.
  RT:       "radiotherapy",
  RTDOSE:   "radiotherapy",
  RTIMAGE:  "radiotherapy",
  RTPLAN:   "radiotherapy",
  RTRECORD: "radiotherapy",
  RTSTRUCT: "radiotherapy",
};

/** Specialty priority so a mixed study (e.g. RT plan + linked CT)
 *  resolves to the specialised reading environment. */
const PRIORITY: Specialty[] = ["radiotherapy", "pathology", "endoscopy", "radiology"];

export function specialtyForModality(code: string | undefined | null): Specialty {
  if (!code) return "radiology";
  const s = MOD_TO_SPECIALTY[code.toUpperCase()];
  return s ?? "radiology";
}

export function specialtyForStudy(modalities: Array<string | undefined | null>): Specialty {
  const set = new Set(modalities.map((m) => specialtyForModality(m)));
  return PRIORITY.find((s) => set.has(s)) ?? "radiology";
}

export const SPECIALTY_CONFIGS: Record<Specialty, SpecialtyConfig> = {
  radiology: {
    id: "radiology",
    label: "Radiology",
    accent: "brand",
    toolgroups: ["view", "measure", "label", "ml"],
    hangingHint: "radiology",
    reportTemplate: "radiology.impression",
    description: "CT / MR / US / XR / MG — full measurement + AI triage.",
  },
  pathology: {
    id: "pathology",
    label: "Pathology",
    accent: "purple",
    toolgroups: ["wsi", "measure", "label", "ml"],
    hangingHint: "pathology",
    reportTemplate: "pathology.microscopic",
    description: "WSI H&E / IHC / FISH / NGS — tile-based magnification viewer.",
  },
  endoscopy: {
    id: "endoscopy",
    label: "Endoscopy",
    accent: "amber",
    toolgroups: ["video", "label", "ml"],
    hangingHint: "endoscopy",
    reportTemplate: "endoscopy.findings",
    description: "EGD / colonoscopy / bronchoscopy / EUS — video timeline + captured-stills.",
  },
  radiotherapy: {
    id: "radiotherapy",
    label: "Radiotherapy",
    accent: "sky",
    toolgroups: ["view", "rt", "measure"],
    hangingHint: "radiotherapy",
    reportTemplate: "radiotherapy.plan_review",
    description: "RT plan / dose / structure set — 4-pane MPR + DVH.",
  },
};

export function configFor(s: Specialty): SpecialtyConfig {
  return SPECIALTY_CONFIGS[s];
}
